import type { BuiltinAdapterOptions, PersistenceAdapter, FilterValues } from '../types';
import { buildStorageKey } from './types';

/**
 * Built-in localStorage persistence adapter.
 *
 * Plan Section "Built-in adapter factories":
 *   "Built-in adapter factories enforce tenant + user scoping. Free-form keys are not
 *    supported — a forgotten tenant scope in a shared-workstation scenario could leak
 *    one tenant's filter selections to another tenant's session."
 */
export function createLocalStorageAdapter(options: BuiltinAdapterOptions): PersistenceAdapter {
  const key = buildStorageKey(options);
  return {
    load: () => {
      try {
        const raw = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
        if (!raw) return null;
        return JSON.parse(raw) as FilterValues;
      } catch (err) {
        console.warn(`[FilterBar persistence] Failed to load from localStorage key "${key}":`, err);
        return null;
      }
    },
    save: (values) => {
      try {
        if (typeof window === 'undefined') return;
        const filtered = options.exclude?.length ? omit(values, options.exclude) : values;
        window.localStorage.setItem(key, JSON.stringify(filtered));
      } catch (err) {
        console.warn(`[FilterBar persistence] Failed to save to localStorage key "${key}":`, err);
      }
    },
    clear: () => {
      try {
        if (typeof window === 'undefined') return;
        window.localStorage.removeItem(key);
      } catch (err) {
        console.warn(`[FilterBar persistence] Failed to clear localStorage key "${key}":`, err);
      }
    },
    debounceMs: options.debounceMs ?? 0,
    exclude: options.exclude,
  };
}

function omit(values: FilterValues, keys: string[]): FilterValues {
  const out: FilterValues = {};
  for (const k of Object.keys(values)) {
    if (!keys.includes(k)) out[k] = values[k];
  }
  return out;
}
